import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import translations from '@translationKeys'
import { Nullable } from '@types'

import { CustomTooltip } from '@components/CustomTooltip'
import TruncateLines from '@components/TruncateLines'
import DefaultCompany from '@components/icons/DefaultCompany'
import { ProfileType } from '@modules/search/types'
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos'
import ChatIcon from '@mui/icons-material/Chat'
import { Box, IconButton, Typography } from '@mui/material'
import { useScreenDimensions } from '@shared/hooks/useScreenDimensions'

import { ChatType, LocalChat } from '../types'
import ChatAvatar from './ChatAvatar'
import {
  BackButton,
  ChatAvatarWrapper,
  ChatPanelWrapper,
  ChatPreviewAvatarContainer,
  DefaultCompanyLogoBadgeWrapper,
  MailIconBadgeWrapper,
  OpenedChatInfoWrapper,
  RotatedArrowIcon,
  StyledBadge,
  StyledDragIcon,
  StyledTypography,
} from './index.styles'

const ChatPanel = ({
  unreadCount,
  toggleChatModal,
  isExpanded = false,
  openedChat,
  goBack,
}: {
  unreadCount: number
  isExpanded: boolean
  toggleChatModal: () => void
  openedChat?: Nullable<LocalChat>
  goBack: () => void
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { isSmallMobile } = useScreenDimensions()

  const goToProfile = () => {
    if (openedChat?.otherParticipant) {
      // existing chat with defined participant
      navigate(`/plus/profile/${openedChat.otherParticipant.id}/${openedChat.otherParticipant.type}`)
    } else if (openedChat?.type === ChatType.GROUP) {
      // to be created chat with a company
      navigate(`/plus/profile/${openedChat.withCompanyId}/${ProfileType.company}`)
    } else if (openedChat?.withDelegates?.[0]) {
      // to be created chat with a delegate
      navigate(`/plus/profile/${openedChat?.withDelegates?.[0]}/${ProfileType.delegate}`)
    }
  }

  return (
    <ChatPanelWrapper $isExpanded={isExpanded} $small={isSmallMobile}>
      <Box display="flex" alignItems="center" justifyContent="center" sx={{ userSelect: 'none' }}>
        <StyledDragIcon sx={{ marginRight: '5px' }} className="handle" onMouseDown={e => e.preventDefault()} />

        {openedChat ? (
          <BackButton
            startIcon={<ArrowBackIosIcon sx={{ marginRight: '-10px', marginLeft: '5px' }} />}
            size="small"
            variant="contained"
            color="primary"
            onClick={goBack}
          >
            {t(translations.INBOX)}
          </BackButton>
        ) : (
          <>
            <MailIconBadgeWrapper>
              <StyledBadge badgeContent={unreadCount > 0 ? ' ' : null} color="secondary">
                <ChatIcon />
              </StyledBadge>
            </MailIconBadgeWrapper>
            <StyledTypography variant="caption">{t(translations.INBOX_TITLE)}</StyledTypography>
          </>
        )}
      </Box>

      {openedChat && (
        <OpenedChatInfoWrapper onClick={goToProfile} $isDisabled={!!openedChat.otherParticipant?.isHidden}>
          <ChatPreviewAvatarContainer flex="0 0 auto">
            {openedChat.isIncoming && openedChat.type === ChatType.GROUP && (
              <CustomTooltip title="Group conversation" placement="top" arrow={false} padding="5">
                <div>
                  <DefaultCompanyLogoBadgeWrapper>
                    {!openedChat.companyAvatar ? (
                      <DefaultCompany fontSize="inherit" />
                    ) : (
                      <ChatAvatarWrapper width="20px" height="20px" sx={{ marginRight: '2px' }}>
                        <img style={{ width: 'inherit' }} src={openedChat.companyAvatar} alt="chat-group-conversation-avatar" />
                      </ChatAvatarWrapper>
                    )}
                  </DefaultCompanyLogoBadgeWrapper>
                </div>
              </CustomTooltip>
            )}
            <ChatAvatar chat={openedChat} isBgPrimary />
          </ChatPreviewAvatarContainer>
          {!isSmallMobile && (
            <Box alignSelf="center">
              <Typography variant="caption" color="white" sx={{ marginBottom: '-2px' }}>
                <TruncateLines lines={1} lineHeight={1}>
                  {openedChat.name}
                </TruncateLines>
              </Typography>
              {!!openedChat.companyName && (
                <Typography variant="body1" color="white">
                  <TruncateLines lines={1} lineHeight={1}>
                    {openedChat.companyName}
                  </TruncateLines>
                </Typography>
              )}
            </Box>
          )}
        </OpenedChatInfoWrapper>
      )}

      <Box display="flex" alignItems="center" justifyContent="center">
        {!isSmallMobile && !openedChat && !!unreadCount && (
          <StyledTypography variant="body2">{t(translations.NEW_MESSAGES, { count: unreadCount })}</StyledTypography>
        )}
        <IconButton onClick={toggleChatModal} sx={{ marginLeft: '5px', marginRight: '-5px' }} id="contact-open_inbox">
          <RotatedArrowIcon className={isExpanded ? 'expanded' : ''} htmlColor="#fff" />
        </IconButton>
      </Box>
    </ChatPanelWrapper>
  )
}

export default ChatPanel
